import { useMemo } from 'react';

import { useIsDesktop } from './useIsDesktop';

import { MyPlayersScore } from '@/types/fieldAssistance';

interface IconProps {
  variant?: MyPlayersScore;
  textTooltip?: string | number;
  text?: string | number;
}

export const useIncidents = (player: MyPlayersScore) => {
  const { isMobile } = useIsDesktop();
  return useMemo(() => {
    const incidentsMobile: (string | number)[] = [];
    const incidentsDesktop: IconProps[] = [];
    if (player.goles && player.goles > 0) {
      if (isMobile) {
        incidentsMobile.push(
          player.goles > 1 ? player.goles + ' goles' : player.goles + ' gol'
        );
      } else {
        incidentsDesktop.push({
          variant: { goles: Infinity },
          textTooltip: player.ptosGoles,
          text: player.goles,
        });
      }
    }
    if (player.golesPenal && player.golesPenal > 0) {
      const goles =
        player.golesPenal > 1
          ? player.golesPenal + ' goles de penal'
          : player.golesPenal + ' gol de penal';
      if (isMobile) {
        incidentsMobile.push(goles);
      } else {
        incidentsDesktop.push({
          variant: { golesPenal: Infinity },
          textTooltip: player.ptosGolesPenal,
          text: player.golesPenal >= 1 ? player.golesPenal : '',
        });
      }
    }
    if (player.golesEnContra && player.golesEnContra > 0) {
      if (isMobile) {
        incidentsMobile.push(
          player.golesEnContra > 1
            ? player.golesEnContra + ' goles en contra'
            : player.golesEnContra + ' gol en contra'
        );
      } else {
        incidentsDesktop.push({
          variant: { golesEnContra: Infinity },
          text: player.golesEnContra >= 1 ? player.golesEnContra : '',
          textTooltip: player.ptosGolesEnContra,
        });
      }
    }
    if (player.tarjetaRoja) {
      if (isMobile) {
        incidentsMobile.push('roja');
      } else {
        incidentsDesktop.push({
          variant: { tarjetaRoja: true },
          textTooltip: player.ptosTarjetaRoja,
        });
      }
    }
    if (player.tarjetaAmarilla) {
      if (isMobile) {
        incidentsMobile.push('amarilla');
      } else {
        incidentsDesktop.push({
          variant: { tarjetaAmarilla: true },
          textTooltip: player.ptosTarjetaAmarilla,
        });
      }
    }
    if (player.figura) {
      isMobile
        ? incidentsMobile.push('figura')
        : incidentsDesktop.push({
            variant: { figura: true },
            textTooltip: player.ptosFigura,
          });
    }
    if (player.penalesAtajados && player.penalesAtajados > 0) {
      if (isMobile) {
        incidentsMobile.push(
          player.penalesAtajados > 1
            ? player.penalesAtajados + ' penales atajados'
            : player.penalesAtajados + ' penal atajado'
        );
      } else {
        incidentsDesktop.push({
          variant: { penalesAtajados: Infinity },
          text: player.penalesAtajados >= 1 ? player.penalesAtajados : '',
          textTooltip: player.ptosPenalesAtajados,
        });
      }
    }
    if (player.penalesErrados && player.penalesErrados > 0) {
      if (isMobile) {
        incidentsMobile.push(
          player.penalesErrados > 1
            ? player.penalesErrados + ' penales errados'
            : player.penalesErrados + ' penal errado'
        );
      } else {
        incidentsDesktop.push({
          variant: { penalesErrados: Infinity },
          text: player.penalesErrados >= 1 ? player.penalesErrados : '',
          textTooltip: player.ptosPenalesErrados,
        });
      }
    }
    if (player.golesRecibidos && player.golesRecibidos > 0) {
      if (isMobile) {
        incidentsMobile.push(
          player.golesRecibidos > 1
            ? player.golesRecibidos + ' goles recibidos'
            : player.golesRecibidos + ' gol recibido'
        );
      } else {
        incidentsDesktop.push({
          variant: { golesRecibidos: Infinity },
          text: player.golesRecibidos >= 1 ? player.golesRecibidos : '',
          textTooltip: player.ptosGolesRecibidos,
        });
      }
    }
    if (player.isCapitan) {
      if (isMobile) {
        incidentsMobile.push('capitán');
      } else {
        incidentsDesktop.push({
          variant: { isCapitan: true },
          textTooltip: player.ptosClarin,
        });
      }
    }
    if (player.golesVisitante && player.golesVisitante > 0) {
      if (isMobile) {
        incidentsMobile.push(
          player.golesVisitante > 1
            ? player.golesVisitante + ' goles de visitante'
            : player.golesVisitante + ' gol de visitante'
        );
      } else {
        incidentsDesktop.push({
          variant: { golesVisitante: Infinity },
          text: player.golesVisitante >= 1 ? player.golesVisitante : '',
          textTooltip: player.ptosVisitante,
        });
      }
    }

    if (player.isEstadistico) {
      if (isMobile) {
        incidentsMobile.push('No sumó puntos');
      } else {
        incidentsDesktop.push({
          variant: { jugo: true },
          textTooltip: 'No sumó puntos',
        });
      }
    }

    if (player.asegurado) {
      if (isMobile) {
        incidentsMobile.push('asegurado ' + player.ptosAsegurado);
      } else {
        incidentsDesktop.push({
          variant: { asegurado: true },
          textTooltip: player.ptosAsegurado,
        });
      }
    }
    if (player.vallaInvicta) {
      if (isMobile) {
        incidentsMobile.push('valla invicta');
      } else {
        incidentsDesktop.push({
          variant: { vallaInvicta: true },
          textTooltip:
            player.ptosVallaInvicta !== 0 ? player.ptosVallaInvicta : '',
        });
      }
    }
    if (player.golDeOro) {
      if (isMobile) {
        incidentsMobile.push('gol de oro');
      } else {
        incidentsDesktop.push({
          variant: { golDeOro: true },
          textTooltip: player.ptosGolDeOro,
        });
      }
    }

    if (isMobile) {
      return { incidentsMobile };
    }

    return { incidentsDesktop };
  }, [isMobile, player]);
};
